import React, { createContext, useContext, useMemo } from "react";
import { useSelector } from "react-redux";

import { RootState } from "store/rootReducer";

import { useUser } from "hooks";

import { useProjectListQuery } from "components/project/projects/queries";
import { getProjectPermissions } from "components/project/projects/utils";

interface ProjectPermissionsContextType {
  permissions: ReturnType<typeof getProjectPermissions> | null;
  isLoading: boolean;
}

const ProjectPermissionsContext = createContext<ProjectPermissionsContextType>({
  permissions: null,
  isLoading: false
});

export function ProjectPermissionsProvider({ children }: { children: React.ReactNode }) {
  const { isAtLeastPowerUser, isAtLeastAdmin, isReadonly, user } = useUser();

  const selectedProjectId = useSelector(
    (state: RootState) => state.groupBy.selectedProjectIdForColumns
  );

  const projectListQuery = useProjectListQuery({
    staleTime: 20000
  });

  const value = useMemo(() => {
    if (!selectedProjectId || projectListQuery.isLoading || !projectListQuery.data) {
      return {
        permissions: null,
        isLoading: projectListQuery.isLoading
      };
    }

    const selectedProject = projectListQuery.data.find(
      (project) => project.projectId === selectedProjectId
    );

    if (!selectedProject) {
      return {
        permissions: null,
        isLoading: false
      };
    }

    const permissions = getProjectPermissions(
      selectedProject,
      user,
      isAtLeastPowerUser,
      isAtLeastAdmin,
      isReadonly
    );

    return {
      permissions,
      isLoading: false
    };
  }, [
    selectedProjectId,
    projectListQuery.data,
    projectListQuery.isLoading,
    user,
    isAtLeastPowerUser,
    isAtLeastAdmin,
    isReadonly
  ]);

  return (
    <ProjectPermissionsContext.Provider value={value}>
      {children}
    </ProjectPermissionsContext.Provider>
  );
}

export function useProjectPermissions() {
  const context = useContext(ProjectPermissionsContext);
  if (context === undefined) {
    throw new Error(
      "useProjectPermissions must be used within a ProjectPermissionsProvider"
    );
  }
  return context;
}
